import Phaser from 'phaser';
import { HyveClient } from '@hyve-sdk/js';
import type { HyvePhaserPluginConfig, HyveAuthEvent, HyveTelemetryEvent, HyveApiEvent } from './types';
import type { TelemetryAdditionalData, Inventory, InventoryItem } from '@hyve-sdk/js';
import { HyveEvents } from './events';

/**
 * Hyve Phaser Plugin - Global plugin for Hyve SDK integration
 * 
 * @example
 * ```javascript
 * // In game config
 * const config = {
 *   plugins: {
 *     global: [{
 *       key: 'HyvePhaserPlugin',
 *       plugin: HyvePhaserPlugin,
 *       start: true,
 *       data: {
 *         apiKey: 'your-api-key',
 *         isDev: true
 *       }
 *     }]
 *   }
 * };
 * ```
 */
export default class HyvePhaserPlugin extends Phaser.Plugins.BasePlugin {
  private client!: HyveClient;
  private config!: HyvePhaserPluginConfig;
  private authenticated: boolean = false;
  private debug: boolean = false;

  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);
  }

  /**
   * Initialize the plugin with configuration
   */
  init(data?: HyvePhaserPluginConfig): void {
    this.config = {
      isDev: true,
      debug: false,
      ...data
    };

    this.debug = this.config.debug || false;
    this.log('Plugin initializing with config:', this.config);

    // Initialize Hyve Client
    this.client = new HyveClient({
      apiKey: this.config.apiKey,
      isDev: this.config.isDev
    });

    // Always auto-authenticate
    this.authenticateFromUrl();

    // Emit ready event
    this.game.events.emit(HyveEvents.PLUGIN_READY, this);
    this.log('Plugin initialized and ready');
  }

  /**
   * Start the plugin
   */
  start(): void {
    this.log('Plugin started');
  }

  /**
   * Stop the plugin
   */
  stop(): void {
    this.log('Plugin stopped');
  }

  /**
   * Destroy the plugin
   */
  destroy(): void {
    this.client.logout();
    super.destroy();
    this.log('Plugin destroyed');
  }

  /**
   * Authenticate user from URL parameters
   */
  async authenticateFromUrl(): Promise<boolean> {
    try {
      this.log('Attempting auto-authentication from URL params...');
      
      const authenticated = await this.client.authenticateFromUrl();
      this.authenticated = authenticated;
      
      const authEvent: HyveAuthEvent = {
        success: authenticated,
        userId: this.client.getUserId()
      };

      if (authenticated) {
        this.log('Authentication successful:', authEvent.userId);
      } else {
        this.log('Authentication failed - no valid credentials in URL');
        authEvent.error = 'No valid credentials in URL parameters';
      }

      // Emit authentication event
      this.game.events.emit(HyveEvents.AUTHENTICATED, authEvent);
      
      return authenticated;
    } catch (error) {
      this.logError('Authentication error:', error);
      
      const authEvent: HyveAuthEvent = {
        success: false,
        userId: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      this.game.events.emit(HyveEvents.AUTHENTICATED, authEvent);
      return false;
    }
  }


  /**
   * Send raw telemetry event
   */
  async sendTelemetry(
    location: string,
    category: string,
    action: string,
    subCategory?: string | null,
    subAction?: string | null,
    details?: string | null,
    additionalData?: TelemetryAdditionalData | null
  ): Promise<boolean> {
    try {
      const eventName = `${category}.${action}`;
      this.log(`Sending telemetry: ${eventName}`);

      const success = await this.client.sendTelemetry(
        location,
        category,
        action,
        subCategory,
        subAction,
        details,
        additionalData
      );

      const telemetryEvent: HyveTelemetryEvent = {
        success,
        eventName
      };

      if (success) {
        this.log(`Telemetry sent successfully: ${eventName}`);
        this.game.events.emit(HyveEvents.TELEMETRY_SENT, telemetryEvent);
      } else {
        this.logError(`Failed to send telemetry: ${eventName}`);
        telemetryEvent.error = 'Failed to send telemetry';
        this.game.events.emit(HyveEvents.TELEMETRY_ERROR, telemetryEvent);
      }

      return success;
    } catch (error) {
      this.logError('Telemetry error:', error);
      
      const telemetryEvent: HyveTelemetryEvent = {
        success: false,
        eventName: `${category}.${action}`,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      this.game.events.emit(HyveEvents.TELEMETRY_ERROR, telemetryEvent);
      return false;
    }
  }

  /**
   * Make an authenticated API call using JWT token from URL params
   * @param endpoint API endpoint path (e.g., '/api/v1/users/profile')
   * @param options Fetch options (method, body, etc.)
   * @returns Promise resolving to the API response
   */
  async callApi<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      this.log(`Making API call to: ${endpoint}`);

      const data = await this.client.callApi<T>(endpoint, options);

      const apiEvent: HyveApiEvent<T> = {
        success: true,
        endpoint,
        data
      };

      this.log(`API call successful: ${endpoint}`);
      this.game.events.emit(HyveEvents.API_CALL_SUCCESS, apiEvent);

      return data;
    } catch (error) {
      this.logError(`API call failed: ${endpoint}`, error);

      const apiEvent: HyveApiEvent = {
        success: false,
        endpoint,
        error: error instanceof Error ? error.message : 'Unknown error'
      };

      this.game.events.emit(HyveEvents.API_CALL_ERROR, apiEvent);
      throw error;
    }
  }

  /**
   * Get the user's inventory
   * @returns Promise resolving to the user's inventory
   */
  async getInventory(): Promise<Inventory> {
    try {
      this.log('Fetching user inventory');
      const inventory = await this.client.getInventory();
      this.log('Inventory fetched successfully:', inventory.total_count, 'items');
      return inventory;
    } catch (error) {
      this.logError('Failed to fetch inventory:', error);
      throw error;
    }
  }

  /**
   * Get a specific inventory item by ID
   * @param itemId The inventory item ID
   * @returns Promise resolving to the inventory item
   */
  async getInventoryItem(itemId: string): Promise<InventoryItem> {
    try {
      this.log('Fetching inventory item:', itemId);
      const item = await this.client.getInventoryItem(itemId);
      this.log('Inventory item fetched successfully:', item.item_type);
      return item;
    } catch (error) {
      this.logError('Failed to fetch inventory item:', error);
      throw error;
    }
  }

  /**
   * Check if JWT token is available
   */
  hasJwtToken(): boolean {
    return this.client.hasJwtToken();
  }

  /**
   * Get the current JWT token
   */
  getJwtToken(): string | null {
    return this.client.getJwtToken();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.authenticated && this.client.isUserAuthenticated();
  }

  /**
   * Get the current user ID
   */
  getUserId(): string | null {
    return this.client.getUserId();
  }

  /**
   * Get the current session ID
   */
  getSessionId(): string {
    return this.client.getSessionId();
  }

  /**
   * Get the underlying HyveClient instance
   */
  getClient(): HyveClient {
    return this.client;
  }

  /**
   * Configure the plugin after initialization
   */
  configure(config: HyvePhaserPluginConfig): void {
    this.config = { ...this.config, ...config };
    this.debug = this.config.debug || false;
    
    // Update client configuration if needed
    if (config.apiKey !== undefined || config.isDev !== undefined) {
      this.client.updateTelemetryConfig({
        apiKey: config.apiKey,
        isDev: config.isDev
      });
    }
    
    this.log('Plugin reconfigured:', this.config);
  }

  /**
   * Log message if debug is enabled
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[HyvePhaserPlugin]', ...args);
    }
  }

  /**
   * Log error message
   */
  private logError(...args: any[]): void {
    console.error('[HyvePhaserPlugin]', ...args);
  }
}

// Export types and events for TypeScript users
export { HyveEvents } from './events';
export type {
  HyvePhaserPluginConfig,
  HyveAuthEvent,
  HyveTelemetryEvent,
  HyveApiEvent
} from './types';